/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the 'code splitting' technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

const initHomeCarousel = () => {
  if (!document.querySelector(".cc-product-features-home")) {
    return;
  }
  // 大的整个carousel索引
  let index = 0;
  // 图里小的swiper索引
  let swiperIndex = 0;
  const carousel_items = document.querySelectorAll(
    ".cc-product-features-home .product-features__image"
  );
  const dot_items = document.querySelectorAll(
    ".cc-product-features-home .dot_item"
  );

  dot_items.forEach((item, activeIndex) => {
    item.addEventListener("click", () => {
      index = activeIndex;
      slideChange(index);
    });
  });

  document
    .querySelector(".cc-product-features-home .carousel_prev_btn")
    .addEventListener("click", () => {
      if (index == 0) {
        // index = dot_items.length - 1;
        return;
      } else {
        index--;
      }
      slideChange(index);
    });

  document
    .querySelector(".cc-product-features-home .carousel_next_btn")
    .addEventListener("click", () => {
      if (index == dot_items.length - 1) {
        // index = 0;
        return;
      } else {
        index++;
      }
      slideChange(index);
    });

  document
    .querySelectorAll(".cc-product-features-home .hotspot2_wrap")
    .forEach((item, index) => {
      item.querySelectorAll(".hotspot2").forEach((item2, index2) => {
        item2.addEventListener("click", () => {
          if (item2.classList.contains("is-active")) {
            // 修改swiper-item状态
            document
              .querySelectorAll(".cc-product-features-home .swiper-item")
              .forEach((item) => {
                item.classList.remove("active");
              });

            // 修改x点的状态
            document
              .querySelectorAll(".cc-product-features-home .hotspot2")
              .forEach((item) => {
                item.classList.remove("is-active");
              });
          } else {
            // 修改swiper-item状态
            document
              .querySelectorAll(".cc-product-features-home .swiper-item")
              .forEach((item) => {
                item.classList.remove("active");
              });
            carousel_items[index]
              .querySelectorAll(".swiper-item")
            [index2].classList.add("active");

            // 修改x点的状态
            document
              .querySelectorAll(".cc-product-features-home .hotspot2")
              .forEach((item) => {
                item.classList.remove("is-active");
              });
            item2.classList.add("is-active");
          }
        });
      });
    });

  const slideChange = (activeIndex) => {
    // 设置轮播图
    carousel_items.forEach((item, index) => {
      if (index == activeIndex) {
        item.classList.remove("hidden");
        item.classList.add("block");
      } else {
        item.classList.remove("active");
        item.classList.add("hidden");
      }
    });

    // 设置dot状态
    dot_items.forEach((item) => {
      item.classList.remove("active");
    });
    dot_items[index].classList.add("active");
    index = activeIndex;

    // 设置按钮状态
    if (index == 0) {
      document
        .querySelector(".cc-product-features-home .carousel_prev_btn")
        .classList.add("disabled");
      document
        .querySelector(".cc-product-features-home .carousel_next_btn")
        .classList.remove("disabled");
    } else if (index == dot_items.length - 1) {
      document
        .querySelector(".cc-product-features-home .carousel_next_btn")
        .classList.add("disabled");
      document
        .querySelector(".cc-product-features-home .carousel_prev_btn")
        .classList.remove("disabled");
    } else {
      document
        .querySelector(".cc-product-features-home .carousel_prev_btn")
        .classList.remove("disabled");
      document
        .querySelector(".cc-product-features-home .carousel_next_btn")
        .classList.remove("disabled");
    }

    scrollToItem(activeIndex);
  };

  // let timer = setInterval(() => {
  //   if (index == dot_items.length - 1) {
  //     index = 0;
  //   } else {
  //     index++;
  //   }
  //   slideChange(index);
  // }, 3000);

  // 鼠标移出更新定时器
  const freshTimer = () => {
    // clearInterval(timer);
    slideChange(index);
    // timer = setInterval(() => {
    //   if (index == dot_items.length - 1) {
    //     index = 0;
    //   } else {
    //     index++;
    //   }
    //   slideChange(index);
    // }, 3000);
  };

  // active dot item 居中
  const scrollToItem = (index) => {
    const dot_item = dot_items[index];
    const container = document.querySelector(
      ".cc-product-features-home .dot_wrap"
    );
    const containerWidth = container.clientWidth;
    const itemOffset = dot_item.offsetLeft;
    const itemWidth = dot_item.offsetWidth;

    // 计算需要滚动的距离（居中显示）
    const scrollPosition = itemOffset - (containerWidth - itemWidth) / 2;

    container.scrollTo({
      left: scrollPosition,
      behavior: "smooth",
    });
  };

  // 鼠标移入暂停
  // document
  //   .querySelector(".cc-product-features-home .product-features")
  //   .addEventListener("mouseenter", () => {
  //     clearInterval(timer);
  //   });

  // 鼠标移出继续
  // document
  //   .querySelector(".cc-product-features-home .product-features")
  //   .addEventListener("mouseleave", () => {
  //     freshTimer();
  //   });
};

const initHomeVideo = () => {
  if (!document.querySelector(".cc-promo-video")) {
    return;
  }
  const blocks = document.querySelectorAll(".cc-promo-video .effect-hover1");
  blocks.forEach((item) => {
    const video = item.querySelector("video");
    video?.pause();

    // hover播放视频，移出暂停
    item.addEventListener("mouseenter", () => {
      video?.play();
    });
    item.addEventListener("mouseleave", () => {
      video?.pause();
    });
  });
};

const initYoutubeVideo = () => {
  if (!document.querySelector(".section-video-youtube")) {
    return;
  }
  const videoContent = document.querySelector(
    ".section-video-youtube .video_content"
  );
  const videoContentWrap = document.querySelector(
    ".section-video-youtube .video_wrap"
  );
  document
    .querySelector(".section-video-youtube .play_icon_wrap")
    .addEventListener("click", () => {
      videoContentWrap.classList.add("active");
      if (!videoContentWrap.contains(videoContent)) {
        videoContentWrap.append(videoContent);
      }
    });
  document
    .querySelector(".section-video-youtube .close_icon_wrap")
    .addEventListener("click", () => {
      videoContentWrap.classList.remove("active");
      videoContentWrap.removeChild(videoContent);
    });
};

const initYoutubeVideoPdp = () => {
  if (!document.querySelector(".cc-video-pdp")) {
    return;
  }
  const playIcons = document.querySelectorAll(".cc-video-pdp .play_icon_wrap");
  playIcons.forEach((item) => {
    const videoItem = item.closest(".video-item");
    const videoContent = videoItem.querySelector(".video_content");
    const videoContentWrap = videoItem.querySelector(".video_wrap");
    item.addEventListener("click", () => {
      videoContentWrap.classList.add("active");
      if (!videoContentWrap.contains(videoContent)) {
        videoContentWrap.append(videoContent);
      }
    });
    videoItem
      .querySelector(".close_icon_wrap")
      .addEventListener("click", () => {
        videoContentWrap.classList.remove("active");
        videoContentWrap.removeChild(videoContent);
      });
  });
};

const initHomeTabSwiper = () => {
  if (!document.querySelector(".tab .tab-item")) {
    return;
  }
  const tabs = document.querySelectorAll(".tab:has(.tab-item)");
  window.galleryTabId = [];
  tabs.forEach((tab) => {
    tab.querySelectorAll(".tab-item").forEach((tabItem, index) => {
      const tabContentItems = tabItem.parentElement.parentElement.querySelectorAll(".tab-content");

      tabItem.addEventListener("click", () => {
        tabItem.parentElement.querySelectorAll(".tab-item").forEach((item) => {
          item.classList.remove("is-active");
        });
        tabItem.classList.add("is-active");

        if (window.gallery && tabItem.dataset.id) {
          let id = tabItem.dataset.id
          if (id) {
            if (window.gallery[id] && !window.galleryTabId.includes(id)) {
              window.galleryTabId.push(id);
              setTimeout(() => {
                window.gallery[id]();
              }, 1000);
            }
          }
        }

        tabContentItems.forEach((item) => {
          item.classList.remove("is-active");
        });
        tabContentItems[index].classList.add("is-active");
      });
    });
  });
};

const initHeaderSearch = () => {
  const header = document.querySelector(".header");
  const searchContent = document.querySelector(".my_header_search");
  const searchIcon = document.querySelector(".js-show-search");
  const searchCloseIcon = document.querySelector(
    ".my_header_search .close_search_wrap"
  );
  if (searchIcon) {
    searchIcon.addEventListener("click", () => {
      if (header.classList.contains("my-search-active")) {
        header.classList.remove("my-search-active");
        header.classList.add("search-is-collapsed");
        header.classList.remove("search-is-visible");
      } else {
        header.classList.add("my-search-active");
        header.classList.remove("search-is-collapsed");
        header.classList.add("search-is-visible");
      }
    });
  }

  if (searchCloseIcon) {
    searchCloseIcon.addEventListener("click", () => {
      header.classList.remove("my-search-active");
      header.classList.add("search-is-collapsed");
      header.classList.remove("search-is-visible");
    });
  }

  window.addEventListener("click", (e) => {
    if (searchIcon === e.target || searchIcon.contains(e.target)) {
      return;
    }
    if (!searchContent.contains(e.target)) {
      header.classList.remove("my-search-active");
      header.classList.add("search-is-collapsed");
      header.classList.remove("search-is-visible");
    }
  });
};

const initPdpTab = () => {
  const tabsWrapper = document.querySelector(".product-tab .pdp-tab");
  const tabs = document.querySelectorAll(".product-tab .pdp-tab-item");
  const sections = document.querySelectorAll("[data-pdptab-section]");

  if(tabsWrapper) {
    if(tabsWrapper.scrollWidth > tabsWrapper.clientWidth) {
      tabsWrapper.style.justifyContent = "flex-start"
    }
  }

  let isScrollingByClick = false;
  let scrollTimeout = null;

  const scrollTabToCenter = (tab) => {
    const tabRect = tab.getBoundingClientRect();
    const wrapperRect = tabsWrapper.getBoundingClientRect();
    const wrapperCenter = wrapperRect.width / 2;
    const tabCenter = tabRect.left - wrapperRect.left + tabRect.width / 2;
    const scrollLeft = tabsWrapper.scrollLeft + tabCenter - wrapperCenter;

    tabsWrapper.scrollTo({
      left: scrollLeft,
      behavior: "smooth",
    });
  };

  const setActiveTab = (currentSection) => {
    tabs.forEach((tab) => {
      tab.classList.remove("is-active");
      if (tab.getAttribute("data-section") === currentSection) {
        tab.classList.add("is-active");
        scrollTabToCenter(tab);
      }
    });
  };

  // 点击 tab 滚动到对应区域
  tabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      const targetId = tab.getAttribute("data-section");
      const targetSection = document.querySelector(
        `[data-pdptab-section='${targetId}']`
      );

      if (targetSection) {
        // 设置点击状态，暂时禁用 scroll 激活
        isScrollingByClick = true;

        // 手动激活 tab
        tabs.forEach((t) => t.classList.remove("is-active"));
        tab.classList.add("is-active");
        scrollTabToCenter(tab);
        const targetPosition = targetSection.offsetTop - 160;
        window.scrollTo({
          top: targetPosition,
          behavior: "smooth",
        });

        // 设置一个延时取消点击滚动标志（等滚动结束）
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          isScrollingByClick = false;
        }, 1000);
      }
    });
  });

  window.addEventListener("scroll", () => {
    if (isScrollingByClick) return; // 如果是点击触发的滚动，就不自动切换 tab

    let currentSection = "";

    sections.forEach((section) => {
      const sectionTop = section.offsetTop;

      // 如果页面滚动位置大于等于section顶部（减去一些偏移量）
      if (window.pageYOffset >= sectionTop - 200) {
        currentSection = section.getAttribute("data-pdptab-section");
      }
    });

    if (currentSection) {
      setActiveTab(currentSection);
    }
  });


  const reviewSection = document.querySelector(
    `[data-pdptab-section='reviews']`
  );

  if (!reviewSection) {
    return
  }
  const observer = new IntersectionObserver(
    ([entry]) => {
      const top = entry.boundingClientRect.top;

      if (!entry.isIntersecting && top < 0) {
        // ✅ 元素向上滚出
        tabsWrapper.classList.add("hidden");
      } else {
        // ✅ 元素仍在屏幕中 或 向下滚出
        tabsWrapper.classList.remove("hidden");
      }
    },
    {
      root: null,
      threshold: 0,
    }
  );

  observer.observe(reviewSection);
};

const initPdpTabSwiper = () => {
  if (!document.querySelector(".product-image-tab .tab .tab-item")) {
    return;
  }
  const tabs = document.querySelectorAll(".tab:has(.tab-item)");
  tabs.forEach((tab) => {
    tab.querySelectorAll(".tab-item").forEach((tabItem, index) => {
      const tabContentItems =
        tabItem.parentElement.parentElement.parentElement.querySelectorAll(
          ".tab-content"
        );
      tabItem.addEventListener("click", () => {
        tabItem.parentElement.querySelectorAll(".tab-item").forEach((item) => {
          item.classList.remove("is-active");
        });
        tabItem.classList.add("is-active");

        tabContentItems.forEach((item) => {
          item.classList.remove("is-active");
        });
        tabContentItems[index].classList.add("is-active");
        document.querySelector(
          ".tab .tab-bg-block"
        ).style.transform = `translate(${index * 100}%, 0)`;
      });
    });
  });
};

const initAccount = () => {
  if (!document.querySelector(".account-userinfo")) {
    return;
  }
  const container = document.querySelector(".account-userinfo");
  document
    .querySelector(".my-benefits-container .view-benefits")
    .addEventListener("mouseenter", () => {
      container.classList.add("is-open");
    });
  document
    .querySelector(".my-benefits-container .view-benefits")
    .addEventListener("mouseleave", () => {
      if (container.classList.contains("is-open")) {
        container.classList.remove("is-open");
      }
    });
};

const initPdpRewardMore = () => {
  const rewardMoreEl = document.querySelectorAll(".reward-rays-more");

  rewardMoreEl.forEach(function (item) {
    item.onclick = function () {
      Swal.fire({
        html: document.querySelector(".reward-rays-dialog").innerHTML,
        padding: "0",
        position: "",
        showCloseButton: true,
        showCancelButton: false,
        showConfirmButton: false,
        customClass: {
          container: "cart-success-container pdp-learnmore-container",
          popup: "cart-success-popup pdp-learnmore",
        },
      });
    };
  });
};

const initPdpContactForm = () => {
  document.querySelectorAll(".handle-bulk").forEach(function (item) {
    const bulkModal = document.querySelector(".bulk-modal");
    item.addEventListener("click", function (e) {
      bulkModal.classList.add("open");
    });

    function bindEvent() {
      document.body.addEventListener("submit", function (e) {
        if (
          e.target.id &&
          e.target.id.includes("request-bulk-purchase-prices")
        ) {
          e.preventDefault();
          const formData = new FormData(e.target); // 获取所有字段
          const name = formData.get("name");
          const email = formData.get("email");
          const phone = formData.get("phone");
          const company = formData.get("company");
          const message = formData.get("message");

          let newSubData = {
            leadsName: name,
            leadsEmail: email,
            leadsPhone: phone,
            leadsCompanyName: company,
            customerMessage: message,
            leadsFromSiteAddress: window.location.href,
            countryCode: window.renogyConfig.country,
            leadsPageType: "PDP",
            leadsProductId: "",
            leadsFromSite: window.renogyConfig.leads_from_site,
          };

          // disbaled

          fetch(
            "https://partneradmin.renogy.com/prod-api/api/sc/portal/leadsCustomer/create",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(newSubData),
            }
          )
            .then((response) => {
              return response.json();
            })
            .then((resp) => {
              if (resp.code == 200) {
                document.querySelector('.my-modal-container.bulk-modal').classList.remove('open');
                Swal.fire({
                  html: "<div><div>Thank you!</div><div>Your inquiry has been successfully submitted.</div><div>Our sales team will reach out to you shortly.</div></div>",
                  showConfirmButton: true,
                  customClass: {
                    container:
                      "request-from-layout request-from-layout-success m-request-from-layout",
                    closeButton: "refferal-close-btn",
                  },
                });
              }
            })
            .catch((error) => {
              console.log(error);
              Swal.fire({
                text: "Sorry, Something went wrong Please try again later.",
                iconHtml: `<svg width="60" height="59" viewBox="0 0 60 59" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M29.999 59C13.7071 59 0.5 45.7908 0.5 29.499C0.5 13.2071 13.7071 0 29.999 0C46.2908 0 59.4979 13.2071 59.4979 29.499C59.4979 45.7908 46.2908 59 29.999 59ZM29.999 4.21625C16.0354 4.21625 4.71414 15.5375 4.71414 29.5011C4.71414 43.4646 16.0354 54.7859 29.999 54.7859C43.9625 54.7859 55.2838 43.4646 55.2838 29.5011C55.2838 15.5375 43.9625 4.21625 29.999 4.21625Z" fill="#A4A4A4"/>
                  <path d="M21.9961 21.998L37.9987 38.0007" stroke="#A4A4A4" stroke-width="4" stroke-linecap="square" stroke-linejoin="round"/>
                  <path d="M21.9961 38.0007L37.9987 21.998" stroke="#A4A4A4" stroke-width="4" stroke-linecap="square" stroke-linejoin="round"/>
                  </svg>
                `,
                customClass: {
                  container: "my-swal my-swal-error",
                },
              })
            });
        }
      });
    }

    bindEvent();
  });
};

const initPdp = () => {
  const returnBtn = document.querySelector(".return-refund");
  if (!returnBtn) {
    return;
  }

  const returnContent = document.querySelector(".return-refund-cont");
  returnBtn.addEventListener("click", () => {
    Swal.fire({
      html: returnContent,
      padding: "0",
      position: "",
      showCloseButton: true,
      showCancelButton: false,
      showConfirmButton: false,
      // showClass: {
      //   popup: showClasspopup,
      // },
      // hideClass: {
      //   popup: hideClasspopup,
      // },
      customClass: {
        container: "cart-success-container Battery-Core-popup",
        popup: "cart-success-popup Return-Refund-Policy",
      },
    });
  });
};

const initMyModal = () => {
  const myModals = document.querySelectorAll(".my-modal-container");
  myModals.forEach((myModal) => {
    myModal.querySelector(".close-modal-btn").addEventListener("click", (e) => {
      myModal.classList.remove("open");
    });
  });
};

const initBlog = () => {
  const tabsWrapper = document.querySelector(".blog-category-items");
  const tabs = document.querySelectorAll(".blog-category-items .category-item");
  const sections = document.querySelectorAll(".cc-main-blog");
  let isScrollingByClick = false;
  let scrollTimeout = null;
  const scrollTabToCenter = (tab) => {
    const tabRect = tab.getBoundingClientRect();
    const wrapperRect = tabsWrapper.getBoundingClientRect();
    const wrapperCenter = wrapperRect.width / 2;
    const tabCenter = tabRect.left - wrapperRect.left + tabRect.width / 2;
    const scrollLeft = tabsWrapper.scrollLeft + tabCenter - wrapperCenter;

    tabsWrapper.scrollTo({
      left: scrollLeft,
      behavior: "smooth",
    });
  };
  // 点击tab滚动到对应区域
  tabs.forEach((tab, index) => {
    tab.addEventListener("click", () => {
      // 设置点击状态，暂时禁用 scroll 激活
      isScrollingByClick = true;
      const targetSection = sections[index];

      if (targetSection) {
        const targetPosition = targetSection.offsetTop - 160;
        window.scrollTo({
          top: targetPosition,
          behavior: "smooth",
        });

        // 更新active状态
        tabs.forEach((t) => t.classList.remove("active"));
        tab.classList.add("active");
        scrollTabToCenter(tab);

        // 设置一个延时取消点击滚动标志（等滚动结束）
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          isScrollingByClick = false;
        }, 1000);
      }
    });
  });

  window.addEventListener("scroll", () => {
    if (isScrollingByClick) return; // 如果是点击触发的滚动，就不自动切换 tab
    let currentSection = "";

    sections.forEach((section, index) => {
      const sectionTop = section.offsetTop;

      // 如果页面滚动位置大于等于section顶部（减去一些偏移量）
      if (window.pageYOffset >= sectionTop - 160) {
        currentSection = index;
      }
    });

    // 更新tab的active状态
    tabs.forEach((tab, index) => {
      tab.classList.remove("active");
      if (index === currentSection) {
        tab.classList.add("active");
        scrollTabToCenter(tab);
      }
    });
  });
};

const initBlogModal = () => {
  const wrap = document.querySelector(".anchor-wrap");
  if (!wrap) {
    return;
  }
  document.querySelectorAll(".article__content table").forEach((table) => {
    const wrapper = document.createElement("div");
    wrapper.className = "scrollable-table";
    table.parentNode.insertBefore(wrapper, table);
    wrapper.appendChild(table);
  });

  const mask = document.querySelector(".js-overlay");

  document.querySelectorAll(".article-detail-wrap .close-btn").forEach((item) =>
    item.addEventListener("click", () => {
      document.querySelector(".anchor-wrap").classList.add("close");
      if (window.innerWidth < 768) {
        mask.classList.remove("is-visible");
      }
    })
  );
  document.querySelectorAll(".article-detail-wrap .open-btn").forEach((item) =>
    item.addEventListener("click", () => {
      document.querySelector(".anchor-wrap").classList.remove("close");
      if (window.innerWidth < 768) {
        mask.classList.add("is-visible");
      }
    })
  );

  if (window.innerWidth < 768) {
    document.querySelector(".anchor-wrap").classList.add("close");
    mask.addEventListener("click", (e) => {
      mask.classList.remove("is-visible");
      document.querySelector(".anchor-wrap").classList.add("close");
    });
  } else {
    document.querySelector(".anchor-wrap").classList.remove("close");
  }

  const sections = document.querySelectorAll(".article__content h2");
  if (sections.length != 0) {
    document.querySelector(".anchor-wrap").classList.add("has-data");
  }
  const wrapper = document.querySelector(".article-detail-wrap .anchor-items");
  sections.forEach((item) => {
    const itemWrap = document.createElement("div");
    itemWrap.innerText = item.innerText;
    itemWrap.className = "anchor-item";
    wrapper.appendChild(itemWrap);
  });

  let isScrollingByClick = false;
  let scrollTimeout = null;

  const tabs = document.querySelectorAll(".article-detail-wrap .anchor-item");
  // 点击anchor滚动到对应区域
  tabs.forEach((tab, index) => {
    tab.addEventListener("click", () => {
      const targetSection = sections[index];
      if (targetSection) {
        // 设置点击状态，暂时禁用 scroll 激活
        isScrollingByClick = true;

        // 点击滚动到顶部（减去一些偏移量）
        const targetPosition = targetSection.offsetTop - 120;
        window.scrollTo({
          top: targetPosition,
          behavior: "smooth",
        });

        // 更新active状态
        tabs.forEach((t) => t.classList.remove("active"));
        tab.classList.add("active");
        updateScrollBar();

        // 设置一个延时取消点击滚动标志（等滚动结束）
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          isScrollingByClick = false;
        }, 1000);
      }
    });
  });

  window.addEventListener("scroll", () => {
    if (isScrollingByClick) return; // 如果是点击触发的滚动，就不自动切换 tab

    let currentSection = "";

    sections.forEach((section, index) => {
      const sectionTop = section.offsetTop;

      // 如果页面滚动位置大于等于section顶部（减去一些偏移量）
      if (window.pageYOffset >= sectionTop - 120) {
        currentSection = index;
      }
    });

    // 更新tab的active状态
    tabs.forEach((tab, index) => {
      tab.classList.remove("active");
      if (index === currentSection) {
        tab.classList.add("active");
      }
    });
    updateScrollBar();
  });

  const updateScrollBar = () => {
    const activeTab = document.querySelector(
      ".article-detail-wrap .anchor-item.active"
    );
    const scrollBar = document.querySelector(
      ".article-detail-wrap .scroll-bar"
    );
    if (activeTab && scrollBar) {
      scrollBar.style.height = activeTab.offsetHeight + "px";
      scrollBar.style.top = activeTab.offsetTop + "px";
    }
  };
};

const initNewPdpTab = () => {
  const tabComponent = document.querySelectorAll(".tab-bar-wrap")
  tabComponent.forEach((item, index) => {
    const tabItems = item.querySelectorAll(
      ".tab-bar .tab-item"
    );
    const tabContents = item.querySelectorAll(
      ".tab-content-new .item"
    );
    const tabScroll = item.querySelector(
      ".tab-bar .tab-scroll"
    );
    const tabBar = item.querySelector('.tab-bar');

    const updateIndicator = (tabEl) => {
      if (tabEl && tabScroll) {
        tabScroll.style.width = `${tabEl.offsetWidth - 4}px`;
        tabScroll.style.transform = `translateX(${tabEl.offsetLeft}px)`;
      }
    };

    let initialActiveIndex = Array.from(tabContents).findIndex(content => content.classList.contains('active'));
    if (initialActiveIndex === -1) {
      initialActiveIndex = 0;
    }

    setTimeout(() => {
      updateIndicator(tabItems[initialActiveIndex]);
    }, 100);

    tabItems.forEach((item2, index2) => {
      item2.addEventListener("click", () => {
        item.setAttribute("tabindex", index2);

        if (tabBar) {
          tabBar.scrollTo({
            left: item2.offsetLeft - (tabBar.offsetWidth / 2) + (item2.offsetWidth / 2),
            behavior: 'smooth'
          });
        }

        updateIndicator(item2);

        tabContents.forEach((item3, index3) => {
          if (index2 === index3) {
            item3.classList.add("active");
          } else {
            item3.classList.remove("active");
          }
        });
      });
    })
  });
};

const initPdpVideoSwiper = () => {
  const moveItem = document.querySelector(".pdp-desc-video-scroll");
  const maxMove =
    document.querySelectorAll(".pdp-desc-video-scroll iframe").length - 1;
  let start = 0;
  const nextBtn = document.querySelector(".video-to-next");
  const prevBtn = document.querySelector(".video-to-prev");
  if (prevBtn && nextBtn) {
    nextBtn.addEventListener("click", () => {
      if (start < maxMove) {
        start++;
        moveItem.style.transform = `translateX(-${start * 100}%)`;
      }
      if (start === maxMove) {
        nextBtn.classList.add("disabled");
      }
      prevBtn.classList.remove("disabled");
    });

    prevBtn.addEventListener("click", () => {
      if (start > 0) {
        start--;
        moveItem.style.transform = `translateX(-${start * 100}%)`;
      }
      if (start === 0) {
        prevBtn.classList.add("disabled");
      }
      nextBtn.classList.remove("disabled");
    });
  }
};

const initMyChatBtn = () => {
  const btns = document.querySelectorAll(".my_custom_chat_btn")
  btns.forEach(btn => {
    btn.addEventListener("click", () => {
      const realChatBtn = document.querySelector(".float-container .contact-pop")
      if(realChatBtn) {
        realChatBtn.click();
      }
    });
  });
}

const initNav = () => {
  const childNav = document.querySelectorAll(".mega-nav--list details")
  const setChildNavOpen = () => {
    childNav.forEach(nav => {
      nav.open = true
    })
  }
  setChildNavOpen()
  window.addEventListener('on:breakpoint-change', setChildNavOpen())
}

initPdpVideoSwiper();
initNewPdpTab();
// 首页轮播组件
initHomeCarousel();
// 首页视频组件
initHomeVideo();
// youtube视频组件
initYoutubeVideo();
// tab切换组件
initHomeTabSwiper();
// header搜索弹窗
initHeaderSearch();
// pdp视频
initYoutubeVideoPdp();
// pdp tab页切换
initPdpTab();
initPdpTabSwiper();
initAccount();
initPdp();
initPdpRewardMore();
initPdpContactForm();
initMyModal();
initBlog();
initBlogModal();
initMyChatBtn();
initNav();

(function () {
  const sensorsTrack = {
    search() {
      document
        .querySelectorAll(".header__search .search")
        .forEach(function (el) {
          el.addEventListener("submit", function () {
            const val = el["q"].value;
            sensors.track("SearchSubmit", {
              keyword: val ? val : "",
              search_type: "Active",
            });
          });
        });
    },
    init() {
      this.search();
    },
  };

  sensorsTrack.init();
})();
